import { ButtonType } from '../types';
import { sendXsellDepositMessage } from '@/utilities/helpers/wrapperHelpers';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';
import { usePopup } from '../usePopup';
import useDepositFundsAndReloadBalance from '@/unified-modules/wallet/helpers/useAddFundsAndReloadBalance';
import { launchNextGame } from '@/utilities/helpers/launchNextGame';
import { useExitGame } from '@/utilities/helpers/useExitGame';
import { openExternal } from '@/utilities/helpers/openExternalWebView';
import { presentGlobalDepositLimits } from '@/unified-modules/rg/rg-library';
import { BUTTON_TYPE } from '../constants';
import { PLATFORMS } from '@/utilities/constants/game';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import useSoftReload from '@/lostHooks/softReload/useSoftReload';

import {
  TRACK_USER_ACTION_EVENT,
  trackUserAction,
} from '@/utilities/logger/amplitude/tracking';
import {
  GAME_LAUNCH_SOURCE,
  setPendingGameLaunchSource,
} from '@/utilities/logger/snowplow/game-launch-source';
import { useGameLifecycleTracking } from '@/routes/Home/useGameLifecycleTracking';
import { continueWithoutJackpots } from '@/features/SWJ/helpers/jackpot-mode-launch';

import { useNavigate } from 'react-router';
import { handleTryAgain } from '@/utilities/helpers/popupHelpers/tryAgain';
import { FetchGlsGame } from '@/game-client/jackpot-gls-reconcile-run';
import { useGLS } from '@/game-client/hooks/use-gls';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';

export const useButtonAction = (type: ButtonType) => {
  const gameUid = useGameUidParam();
  const navigate = useNavigate();
  const runtime = useStore(useShallow((s) => s.runtime));
  const { wrapperType, platform } = runtime;
  const { supportLink, depositLimitsLink, depositLink } = useStore(
    useShallow((s) => s.commonUIContext),
  );

  const { fetchGame } = useGLS();
  const { emitClientEvent } = useEventManagerEmitter();
  const { hidePopup } = usePopup();
  const { reload } = useSoftReload();
  const depositFundsAndReloadBalance = useDepositFundsAndReloadBalance();
  const exitWithTracking = useExitGame();
  const { fireGameExit } = useGameLifecycleTracking();

  const handleDismiss = () => {
    emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.DISMISS_ERROR);
    hidePopup();
  };

  const depositLimitsHandle = () => {
    if (platform === PLATFORMS.Desktop) {
      presentGlobalDepositLimits();
    }
  };

  const fireInGameExit = () => {
    const gameWindowOpen =
      useStore.getState().gameWindowContext?.gameWindowOpen;
    trackUserAction({
      event: TRACK_USER_ACTION_EVENT.IN_GAME_EXITED,
      module: gameWindowOpen ? 'IN_GAME' : 'IN_GAME_HEADER',
      zone: gameWindowOpen ? 'DASHBOARD' : 'IN_GAME',
    });
  };

  const actionsMap: Map<ButtonType, () => void> = new Map([
    [
      BUTTON_TYPE.DEPOSIT,
      async () => {
        handleDismiss();
        if (platform === PLATFORMS.Desktop) {
          await depositFundsAndReloadBalance();
        } else if (platform === PLATFORMS.Android) {
          window.open(depositLink, '_self');
        }
        sendXsellDepositMessage();
      },
    ],
    [BUTTON_TYPE.TRY_AGAIN, () => handleTryAgain(wrapperType, reload)],
    [BUTTON_TYPE.GO_TO_LOBBY, exitWithTracking],
    [BUTTON_TYPE.DISMISS, handleDismiss],
    [BUTTON_TYPE.SUPPORT, () => openExternal(supportLink)],
    [
      BUTTON_TYPE.DEPOSIT_LIMITS,
      () =>
        platform === PLATFORMS.Desktop
          ? depositLimitsHandle()
          : openExternal(depositLimitsLink),
    ],
    [BUTTON_TYPE.BACK_TO_LAST_GAME, handleDismiss],
    [
      BUTTON_TYPE.FREE_SPIN_CHOOSE_FREE_SPIN,
      () => {
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.FREE_SPINS_DIALOG, 1);
        hidePopup();
      },
    ],
    [
      BUTTON_TYPE.FREE_SPIN_CHOOSE_REAL_MONEY,
      () => {
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.FREE_SPINS_DIALOG, 0);
        hidePopup();
      },
    ],
    [
      BUTTON_TYPE.FREE_SPIN_FINISH_SPINS,
      () => {
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.FREE_SPINS_DIALOG, 0);
        hidePopup();
      },
    ],
    [
      BUTTON_TYPE.GENERAL_ERROR_DISMISS,
      () => {
        hidePopup();
        fireInGameExit();
        exitWithTracking();
      },
    ],
    [
      BUTTON_TYPE.EXIT_GAME_RECENTLY_PLAYED,
      () => {
        hidePopup();
        fireInGameExit();
        fireGameExit();
        setPendingGameLaunchSource(GAME_LAUNCH_SOURCE.RECENTLY_PLAYED_IN_GAME);
        void launchNextGame(reload, navigate);
      },
    ],
    [
      BUTTON_TYPE.EXIT_GAME,
      () => {
        fireInGameExit();
        exitWithTracking();
      },
    ],
    [
      BUTTON_TYPE.CONTINUE_WITHOUT_JACKPOTS,
      () => {
        hidePopup();
        continueWithoutJackpots(
          fetchGame as FetchGlsGame,
          gameUid,
          fireGameExit,
        );
      },
    ],
  ]);

  return {
    clickAction: actionsMap.get(type) ?? (() => {}),
  };
};
